import { useId, useState, type ReactNode } from 'react';
import { GmailMark } from '../email/MailSurface';
import styles from './Integrations.module.css';
export function CalendarMark() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285f4" d="M3 3h18v18H3z" />
      <path fill="#fff" d="M6 6h12v12H6z" />
      <path fill="#34a853" d="M18 18h3l-3 3z" />
      <path fill="#fbbc04" d="M3 18h3v3H3z" />
      <text
        x="12"
        y="15"
        fill="#1a73e8"
        fontSize="8"
        textAnchor="middle"
        fontFamily="sans-serif"
      >
        31
      </text>
    </svg>
  );
}
export function IntegrationBar({
  provider,
  summary,
  action,
  children,
  initiallyOpen = false,
  title,
  description,
  onConnect,
  connecting = false,
  connectionDisabled = false,
  connectionError,
  expandable = true,
  refreshing = false,
}: {
  provider: 'gmail' | 'google_calendar';
  summary: string;
  action?: ReactNode;
  children?: ReactNode;
  initiallyOpen?: boolean;
  title?: string;
  description?: string;
  onConnect?: (() => void) | undefined;
  connecting?: boolean;
  connectionDisabled?: boolean;
  connectionError?: string | undefined;
  expandable?: boolean;
  refreshing?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen),
    id = useId();
  const interactive=!!onConnect || expandable;
  const Control=interactive?'button':'div';
  return (
    <section
      className={styles.section}
      data-expanded={!onConnect && expandable && open}
      data-setup={!!description}
      aria-label={title ?? (provider === 'gmail' ? 'Gmail' : 'Google Calendar')}
    >
      <div className={styles.header}>
        <Control
          className={styles.toggle}
          data-interactive={interactive}
          type={interactive ? "button" : undefined}
          aria-label={onConnect ? title : undefined}
          aria-expanded={onConnect || !expandable ? undefined : open}
          aria-controls={onConnect || !expandable ? undefined : id}
          aria-busy={onConnect ? connecting : undefined}
          disabled={connecting || connectionDisabled}
          onClick={onConnect ?? (expandable ? () => setOpen(v=>!v) : undefined)}
        >
          {provider === 'gmail' ? <GmailMark /> : <CalendarMark />}
          <span className={styles.label}>
            <strong>
              {title ?? (provider === 'gmail' ? 'Gmail' : 'Google Calendar')}
            </strong>
            <span className={styles.summary}>{refreshing ? provider === 'gmail' ? 'Syncing emails…' : 'Syncing meetings…' : description ?? summary}</span>
          </span>
          {refreshing && <span className={styles.refreshSpinner} role="status" aria-label="Refreshing" />}
          {!refreshing && !onConnect && expandable && (
            <svg
              className={styles.chevron}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m9 5 7 7-7 7" />
            </svg>
          )}
        </Control>
        {!onConnect && action && (
          <div className={styles.headerAction}>{action}</div>
        )}
      </div>
      {/* Retain mounted editors through collapse. */}
      {!onConnect && expandable && (
        <div id={id} className={styles.content} hidden={!open}>
          {description && <p className={styles.setupStatus}>{summary}</p>}
          {children}
        </div>
      )}
      {onConnect && connectionError && (
        <p className={styles.connectionError} role="alert">
          {connectionError}
        </p>
      )}
    </section>
  );
}
